import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, AlertCircle, AlertTriangle, X, Loader,
  ChevronDown, ChevronRight, FileText, Eye, Edit2, Check, XCircle,
  ExternalLink, ArrowRight, Shield
} from 'lucide-react';
import { generateClient } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { graphqlClient } from '../../services/graphqlClient';
import { buildSubmitVerificationPayload } from '../../services/apiPayloadBuilder';
import {
  runDocumentVerification,
  runConsistencyVerification,
  ConsistencyVerificationResult,
} from '../../services/guidelinesApiService';
import * as mutations from '../../graphql/mutations';
import * as queries from '../../graphql/queries';
import {
  WorkflowJob,
  JobType,
  StepStatus,
  Loan,
  Document,
  FieldUpdate,
  FieldUpdateAction,
  SubmitVerificationInput,
  GuidelineRecord,
  DocumentVerificationResult,
} from '../../graphql/types';

const client = generateClient();

const isPreviewMode = import.meta.env.VITE_PREVIEW === 'true' ||
  !import.meta.env.VITE_COGNITO_USER_POOL_ID;

/**
 * Extracted field from document processing
 */
interface ExtractedField {
  field: string;
  value: string;
  confidence: number;
  verified?: boolean;
  action?: FieldUpdateAction;
  editedValue?: string;
}

/**
 * Document with parsed extracted fields
 */
interface DocumentWithFields extends Document {
  parsedFields: ExtractedField[];
  /**
   * Optional review status for the document (e.g. "REVIEW_REQUIRED",
   * "REVIEW_COMPLETED", "COMPLETED"). Not part of the base `Document` type;
   * carried locally for the verification UI and preview mocks.
   */
  status?: string;
}

/**
 * Loan with its documents
 */
interface LoanWithDocuments {
  loan: Loan;
  documents: DocumentWithFields[];
}

/**
 * DocumentVerificationStep - Second step in the workflow
 * Displays all processed documents grouped by loan for verification
 * 
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5, 7.6, 7.7, 7.8, 14.3
 */
interface DocumentVerificationStepProps {
  job: WorkflowJob;
  jobType: JobType;
  onStepComplete: () => void;
  onBack?: () => void;
}

const DocumentVerificationStep: React.FC<DocumentVerificationStepProps> = ({
  job,
  jobType,
  onStepComplete,
  onBack,
}) => {
  // State
  const [loansWithDocuments, setLoansWithDocuments] = useState<LoanWithDocuments[]>([]);
  const [expandedLoans, setExpandedLoans] = useState<Set<string>>(new Set());
  const [expandedDocuments, setExpandedDocuments] = useState<Set<string>>(new Set());
  const [selectedDocument, setSelectedDocument] = useState<DocumentWithFields | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [currentJob, setCurrentJob] = useState<WorkflowJob>(job);

  // Document Verification API state
  const [guidelines, setGuidelines] = useState<GuidelineRecord[]>([]);
  const [loadingGuidelines, setLoadingGuidelines] = useState(false);
  const [selectedGuidelineId, setSelectedGuidelineId] = useState<string>('');
  const [verifying, setVerifying] = useState(false);
  const [verificationResults, setVerificationResults] = useState<Record<string, { status: boolean; reason?: string }> | null>(null);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [loadingCachedResults, setLoadingCachedResults] = useState(false);

  // Consistency Verification API state
  const [consistencyVerifying, setConsistencyVerifying] = useState(false);
  const [consistencyResults, setConsistencyResults] = useState<ConsistencyVerificationResult | null>(null);
  const [consistencyError, setConsistencyError] = useState<string | null>(null);
  const [loadingCachedConsistencyResults, setLoadingCachedConsistencyResults] = useState(false);

  // Determine step state
  const isStepNotStarted = currentJob.documentVerificationStatus === StepStatus.NOT_STARTED;
  const isStepInProgress = currentJob.documentVerificationStatus === StepStatus.IN_PROGRESS;
  const isStepCompleted = currentJob.documentVerificationStatus === StepStatus.COMPLETED;

  // Calculate verification progress (Requirement 7.7)
  const totalDocuments = loansWithDocuments.reduce(
    (sum, lwd) => sum + lwd.documents.length, 0
  );
  const verifiedDocuments = loansWithDocuments.reduce(
    (sum, lwd) => sum + lwd.documents.filter(d => isDocumentVerified(d)).length, 0
  );
  const progressPercentage = totalDocuments > 0 ? Math.round((verifiedDocuments / totalDocuments) * 100) : 0;

  // Count documents requiring review (confidence < 80%)
  const documentsRequiringReview = loansWithDocuments.reduce(
    (sum, lwd) => sum + lwd.documents.filter(d => d.confidenceScore < 0.8).length, 0
  );


  /**
   * Check if a document is fully verified
   * A document is verified when all its fields have been accepted, edited, or rejected
   */
  function isDocumentVerified(doc: DocumentWithFields): boolean {
    if (doc.status === "REVIEW_COMPLETED") return true;
    if (doc.parsedFields.length === 0) return true;
    return doc.parsedFields.every(f => f.verified || f.action);
  }

  /**
   * Parse extracted fields from JSON string
   */
  function parseExtractedFields(extractedFields?: string): ExtractedField[] {
    if (!extractedFields) return [];
    try {
      const parsed = JSON.parse(extractedFields);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * Load available guidelines for the tenant
   * For BA jobs, filter by type "Borrower Analysis" (Requirement 4.1, 4.4)
   * For DD jobs, no type filter is applied (existing behavior)
   */
  const loadGuidelines = useCallback(async () => {
    if (isPreviewMode) {
      const previewGuidelines: GuidelineRecord[] = [
        { tenantId: 'platform', guidelineId: 'gl-1', name: 'Fannie Mae Selling Guide', description: 'Standard Fannie Mae eligibility and documentation requirements', type: 'Due Diligence' },
        { tenantId: 'platform', guidelineId: 'gl-2', name: 'Freddie Mac Guide', description: 'Freddie Mac loan purchase eligibility criteria', type: 'Due Diligence' },
        { tenantId: 'platform', guidelineId: 'gl-3', name: 'Fannie Mae BA Guide', description: 'Borrower Analysis guidelines', type: 'Borrower Analysis' },
      ];
      // Apply type filter for BA jobs in preview mode
      if (jobType === JobType.BA) {
        setGuidelines(previewGuidelines.filter(g => g.type === 'Borrower Analysis'));
      } else {
        setGuidelines(previewGuidelines);
      }
      return;
    }
    setLoadingGuidelines(true);
    try {
      const session = await fetchAuthSession();
      const tenantId = session.tokens?.idToken?.payload['custom:tenantId'] as string || 'default-tenant';
      // For BA jobs, pass type filter; for DD jobs, no filter (Requirement 4.1)
      const typeFilter = jobType === JobType.BA ? 'Borrower Analysis' : undefined;
      const result = await client.graphql({
        query: queries.listGuidelines,
        variables: { tenantId, type: typeFilter },
      }) as any;
      setGuidelines(result.data?.listGuidelines || []);
    } catch (err) {
      console.error('Failed to load guidelines:', err);
    } finally {
      setLoadingGuidelines(false);
    }
  }, [jobType]);

  useEffect(() => { loadGuidelines(); }, [loadGuidelines]);

  // ─── Auto-select last used guideline for this job ───
  useEffect(() => {
    if (guidelines.length === 0 || !job.jobId) return;
    
    // Try to load the last used guideline for this job from localStorage
    const lastGuidelineKey = `lastGuideline_${job.jobId}`;
    const lastGuidelineId = localStorage.getItem(lastGuidelineKey);
    
    if (lastGuidelineId && guidelines.some(g => g.guidelineId === lastGuidelineId)) {
      setSelectedGuidelineId(lastGuidelineId);
      console.log(`Auto-selected last used guideline: ${lastGuidelineId}`);
    }
  }, [guidelines, job.jobId]);

  // ─── Save selected guideline to localStorage ───
  useEffect(() => {
    if (selectedGuidelineId && job.jobId) {
      localStorage.setItem(`lastGuideline_${job.jobId}`, selectedGuidelineId);
    }
  }, [selectedGuidelineId, job.jobId]);

  // ─── Load cached results when guideline is selected ───
  useEffect(() => {
    const loadCachedResults = async () => {
      console.log('🔍 [DocumentVerification] loadCachedResults triggered', {
        selectedGuidelineId,
        jobId: job.jobId,
        isPreviewMode
      });
      
      if (!selectedGuidelineId || !job.jobId || isPreviewMode) {
        console.log('⏭️ [DocumentVerification] Skipping cache load:', {
          hasGuideline: !!selectedGuidelineId,
          hasJobId: !!job.jobId,
          isPreview: isPreviewMode
        });
        return;
      }

      if (currentJob.documentVerificationStatus !== StepStatus.COMPLETED) {
        setVerificationResults(null);
        return;
      }
      
      // Clear previous results immediately so stale data doesn't linger
      setVerificationResults(null);
      setLoadingCachedResults(true);
      try {
        const session = await fetchAuthSession();
        const tenantId = session.tokens?.idToken?.payload['custom:tenantId'] as string || 'default-tenant';
        
        console.log('📡 [DocumentVerification] Calling getDocumentVerificationResults', {
          jobId: job.jobId,
          guidelineId: selectedGuidelineId,
          tenantId
        });
        
        const result = await client.graphql({
          query: queries.getDocumentVerificationResults,
          variables: { jobId: job.jobId, tenantId, guidelineId: selectedGuidelineId },
        }) as any;
        
        const cached = result.data?.getDocumentVerificationResults;
        
        console.log('✅ [DocumentVerification] Received response:', cached);
        
        if (cached?.results) {
          // Convert array format back to object format for compatibility
          const resultsObj: Record<string, { status: boolean; reason?: string }> = {};
          cached.results.forEach((r: DocumentVerificationResult) => {
            resultsObj[r.documentName] = {
              status: r.status,
              reason: r.reason,
            };
          });
          setVerificationResults(resultsObj);
          console.log(`✅ [DocumentVerification] Loaded ${cached.results.length} cached results for guideline ${selectedGuidelineId}`);
        } else {
          // Ensure results are cleared when no cached data exists for this guideline
          setVerificationResults(null);
          console.log('⚠️ [DocumentVerification] No cached document verification results found');
        }
      } catch (err) {
        console.error('❌ [DocumentVerification] Failed to load cached results:', err);
        // Not a critical error - user can still run verification
      } finally {
        setLoadingCachedResults(false);
      }
    };
    
    loadCachedResults();
  }, [selectedGuidelineId, job.jobId, currentJob.documentVerificationStatus]);

  // ─── Load cached consistency results when guideline is selected ───
  useEffect(() => {
    const loadCachedConsistencyResults = async () => {
      if (!selectedGuidelineId || !job.jobId || isPreviewMode) {
        return;
      }

      if (currentJob.documentVerificationStatus !== StepStatus.COMPLETED) {
        setConsistencyResults(null);
        return;
      }

      setConsistencyResults(null);
      setLoadingCachedConsistencyResults(true);
      try {
        const session = await fetchAuthSession();
        const tenantId = session.tokens?.idToken?.payload['custom:tenantId'] as string || 'default-tenant';

        const result = await client.graphql({
          query: queries.getConsistencyVerificationResults,
          variables: { jobId: job.jobId, tenantId, guidelineId: selectedGuidelineId },
        }) as any;

        const cached = result.data?.getConsistencyVerificationResults;

        if (cached?.results) {
          const resultsObj: Record<string, { status: boolean; reason?: string }> = {};
          cached.results.forEach((r: { checkName: string; status: boolean; reason?: string }) => {
            resultsObj[r.checkName] = {
              status: r.status,
              reason: r.reason,
            };
          });
          setConsistencyResults(resultsObj);
        } else {
          setConsistencyResults(null);
        }
      } catch (err) {
        console.error('Failed to load cached consistency results:', err);
      } finally {
        setLoadingCachedConsistencyResults(false);
      }
    };

    loadCachedConsistencyResults();
  }, [selectedGuidelineId, job.jobId, currentJob.documentVerificationStatus]);

  /**
   * Call POST /guidelines/document_verification and display results
   */
  const handleRunVerification = async () => {
    if (!selectedGuidelineId) {
      setVerificationError('Please select a guideline');
      return;
    }
    setVerifying(true);
    setVerificationError(null);
    setVerificationResults(null);

    try {
      if (isPreviewMode) {
        await new Promise(r => setTimeout(r, 1500));
        setVerificationResults({
          'W-2 Form': { status: true },
          'Bank Statement': { status: true },
          'Income Statement': { status: false, reason: 'Missing year-to-date figures' },
        });
        setVerifying(false);
        return;
      }

      const results = await runDocumentVerification({
        guideline_id: selectedGuidelineId,
        job_id: job.jobId,
      });
      setVerificationResults(results);
    } catch (err: any) {
      console.error('Document verification failed:', err);
      setVerificationError(err?.message || 'Document verification failed. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  /**
   * Call POST /guidelines/consistency_verification and display results
   */
  const handleRunConsistencyVerification = async () => {
    if (!selectedGuidelineId) {
      setConsistencyError('Please select a guideline');
      return;
    }
    setConsistencyVerifying(true);
    setConsistencyError(null);
    setConsistencyResults(null);

    try {
      if (isPreviewMode) {
        await new Promise(r => setTimeout(r, 1500));
        setConsistencyResults({
          'income_matches_across_documents': { status: true, reason: 'Income figures are consistent across W-2 and pay stubs.' },
          'employment_dates_consistent': { status: true, reason: 'Employment dates align across all documents.' },
          'borrower_name_matches': { status: false, reason: 'Borrower name on W-2 does not match the name on the application.' },
        });
        setConsistencyVerifying(false);
        return;
      }

      const results = await runConsistencyVerification({
        guideline_id: selectedGuidelineId,
        job_id: job.jobId,
      });
      setConsistencyResults(results);
    } catch (err: any) {
      console.error('Consistency verification failed:', err);
      setConsistencyError(err?.message || 'Consistency verification failed. Please try again.');
    } finally {
      setConsistencyVerifying(false);
    }
  };

  /**
   * Load job data including loans and documents
   */
  const loadJobData = useCallback(async () => {
    if (!job.jobId || isPreviewMode) {
      // Preview mode mock data
      if (isPreviewMode) {
        const mockLoans: LoanWithDocuments[] = [
          {
            loan: {
              loanId: 'loan-1',
              jobId: job.jobId,
              tenantId: 'mock-tenant',
              username: 'mock-user',
              cognitoSub: 'mock-sub',
              loanNumber: 'Loan 001',
              borrowerName: 'John Smith',
              propertyAddress: '123 Main St',
              fileCount: 2,
              status: 'COMPLETED' as any,
              createdAt: new Date().toISOString(),
              processedFileCount: 3,
              failedFileCount: 0,
            },
            documents: [
              {
                documentId: 'doc-1',
                fileId: 'file-1',
                loanId: 'loan-1',
                jobId: job.jobId,
                tenantId: 'mock-tenant',
                username: 'mock-user',
                cognitoSub: 'mock-sub',
                sourceFilename: 'income_statement.pdf',
                pageRange: '1-3',
                documentType: 'Income Statement',
                status: "REVIEW_REQUIRED",
                confidenceScore: 0.72,
                requiresHumanReview: true,
                createdAt: new Date().toISOString(),
                parsedFields: [
                  { field: 'Gross Income', value: '$85,000', confidence: 0.95 },
                  { field: 'Net Income', value: '$62,500', confidence: 0.68 },
                  { field: 'Tax Year', value: '2023', confidence: 0.99 },
                ],
              },
              {
                documentId: 'doc-2',
                fileId: 'file-1',
                loanId: 'loan-1',
                jobId: job.jobId,
                tenantId: 'mock-tenant',
                username: 'mock-user',
                cognitoSub: 'mock-sub',
                sourceFilename: 'bank_statement.pdf',
                pageRange: '1-5',
                documentType: 'Bank Statement',
                status: "COMPLETED",
                confidenceScore: 0.92,
                requiresHumanReview: false,
                createdAt: new Date().toISOString(),
                parsedFields: [
                  { field: 'Account Number', value: '****4521', confidence: 0.98 },
                  { field: 'Balance', value: '$45,230.00', confidence: 0.94 },
                  { field: 'Statement Date', value: '01/15/2024', confidence: 0.99 },
                ],
              },
            ],
          },
          {
            loan: {
              loanId: 'loan-2',
              jobId: job.jobId,
              tenantId: 'mock-tenant',
              username: 'mock-user',
              cognitoSub: 'mock-sub',
              loanNumber: 'Loan 002',
              borrowerName: 'Jane Doe',
              propertyAddress: '456 Oak Ave',
              fileCount: 1,
              status: 'COMPLETED' as any,
              createdAt: new Date().toISOString(),
              processedFileCount: 1,
              failedFileCount: 0,
            },
            documents: [
              {
                documentId: 'doc-3',
                fileId: 'file-2',
                loanId: 'loan-2',
                jobId: job.jobId,
                tenantId: 'mock-tenant',
                username: 'mock-user',
                cognitoSub: 'mock-sub',
                sourceFilename: 'w2_form.pdf',
                pageRange: '1-2',
                documentType: 'W-2 Form',
                status: "REVIEW_REQUIRED",
                confidenceScore: 0.65,
                requiresHumanReview: true,
                createdAt: new Date().toISOString(),
                parsedFields: [
                  { field: 'Employer Name', value: 'Acme Corp', confidence: 0.88 },
                  { field: 'Wages', value: '$78,500', confidence: 0.55 },
                  { field: 'Federal Tax Withheld', value: '$12,450', confidence: 0.72 },
                ],
              },
            ],
          },
        ];
        setLoansWithDocuments(mockLoans);
        setExpandedLoans(new Set(['loan-1']));
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const session = await fetchAuthSession();
      const tenantId = session.tokens?.idToken?.payload['custom:tenantId'] as string || 'default-tenant';

      // Load loans for this job
      const loansData = await graphqlClient.queries.listLoansByJob(job.jobId, tenantId);
      
      // Load documents for each loan (Requirement 7.1: grouped by loan)
      const loansWithDocs: LoanWithDocuments[] = [];
      
      for (const loan of loansData.items) {
        const docsData = await graphqlClient.queries.listDocumentsByLoan(
          loan.loanId, job.jobId, tenantId
        );
        
        const documentsWithFields: DocumentWithFields[] = docsData.items.map(doc => ({
          ...doc,
          parsedFields: parseExtractedFields(doc.extractedFields),
        }));
        
        loansWithDocs.push({
          loan,
          documents: documentsWithFields,
        });
      }

      setLoansWithDocuments(loansWithDocs);
      
      // Auto-expand first loan
      if (loansWithDocs.length > 0) {
        setExpandedLoans(new Set([loansWithDocs[0].loan.loanId]));
      }
    } catch (err) {
      console.error('Failed to load job data:', err);
      setError('Failed to load documents for verification');
    } finally {
      setLoading(false);
    }
  }, [job.jobId]);

  useEffect(() => {
    loadJobData();
  }, [loadJobData]);

  // Update current job when prop changes
  useEffect(() => {
    setCurrentJob(job);
  }, [job]);

  // Note: onStepComplete is called explicitly by the "Next" button, not automatically.


  /**
   * Handle field action (accept, edit, reject)
   * Requirement 7.4: Allow users to accept, edit, or reject extracted field values
   * Requirement 7.5: Update confidence score to 100% on edit
   */
  const handleFieldAction = (
    documentId: string,
    fieldIndex: number,
    action: FieldUpdateAction,
    editedValue?: string
  ) => {
    setLoansWithDocuments(prev => prev.map(lwd => ({
      ...lwd,
      documents: lwd.documents.map(doc => {
        if (doc.documentId !== documentId) return doc;
        
        const updatedFields = [...doc.parsedFields];
        updatedFields[fieldIndex] = {
          ...updatedFields[fieldIndex],
          action,
          verified: true,
          // Requirement 7.5: Update confidence to 100% on edit
          confidence: action === 'EDIT' ? 1.0 : updatedFields[fieldIndex].confidence,
          editedValue: action === 'EDIT' ? editedValue : undefined,
        };
        
        return {
          ...doc,
          parsedFields: updatedFields,
        };
      }),
    })));
  };

  /**
   * Submit verification for a document
   * Requirement 7.8: Use ApiPayloadBuilder for verification submissions with jobType
   */
  const handleSubmitVerification = async (doc: DocumentWithFields) => {
    if (isPreviewMode) {
      // Preview mode - just mark as verified
      setLoansWithDocuments(prev => prev.map(lwd => ({
        ...lwd,
        documents: lwd.documents.map(d => 
          d.documentId === doc.documentId 
            ? { ...d, status: "REVIEW_COMPLETED" }
            : d
        ),
      })));
      return;
    }

    setSubmitting(doc.documentId);
    setError(null);

    try {
      const session = await fetchAuthSession();
      const tenantId = session.tokens?.idToken?.payload['custom:tenantId'] as string || 'default-tenant';

      // Build field updates from parsed fields
      const fieldUpdates: FieldUpdate[] = doc.parsedFields
        .filter(f => f.action)
        .map((f, idx) => ({
          fieldId: `field-${idx}`,
          value: f.action === 'EDIT' ? (f.editedValue || f.value) : f.value,
          action: f.action!,
        }));

      // Use ApiPayloadBuilder to include jobType (Requirement 7.8)
      const payload: SubmitVerificationInput = buildSubmitVerificationPayload(
        job.jobId,
        tenantId,
        jobType,
        doc.documentId,
        fieldUpdates
      );

      const result = await client.graphql({
        query: mutations.submitVerification,
        variables: { input: payload },
      });

      const updatedJob = (result as any).data?.submitVerification;
      if (updatedJob) {
        setCurrentJob(updatedJob);
      }

      // Update local state
      setLoansWithDocuments(prev => prev.map(lwd => ({
        ...lwd,
        documents: lwd.documents.map(d => 
          d.documentId === doc.documentId 
            ? { ...d, status: "REVIEW_COMPLETED" }
            : d
        ),
      })));

      // Check if all documents are verified (Requirement 7.6)
      const allVerified = loansWithDocuments.every(lwd =>
        lwd.documents.every(d => 
          d.documentId === doc.documentId || isDocumentVerified(d)
        )
      );

      if (allVerified && updatedJob?.documentVerificationStatus === StepStatus.COMPLETED) {
        onStepComplete();
      }
    } catch (err) {
      console.error('Failed to submit verification:', err);
      setError('Failed to submit verification. Please try again.');
    } finally {
      setSubmitting(null);
    }
  };

  /**
   * Toggle loan expansion
   */
  const toggleLoanExpansion = (loanId: string) => {
    setExpandedLoans(prev => {
      const next = new Set(prev);
      next.has(loanId) ? next.delete(loanId) : next.add(loanId);
      return next;
    });
  };

  /**
   * Toggle document expansion
   */
  const toggleDocumentExpansion = (documentId: string) => {
    setExpandedDocuments(prev => {
      const next = new Set(prev);
      next.has(documentId) ? next.delete(documentId) : next.add(documentId);
      return next;
    });
  };

  /**
   * Get confidence badge styling
   * Requirement 7.2: Highlight documents with confidence < 80%
   */
  const getConfidenceBadge = (confidence: number) => {
    const percentage = Math.round(confidence * 100);
    if (confidence >= 0.8) {
      return {
        className: 'bg-green-100 text-green-800',
        label: `${percentage}%`,
      };
    } else if (confidence >= 0.6) {
      return {
        className: 'bg-yellow-100 text-yellow-800',
        label: `${percentage}% - Review Required`,
      };
    } else {
      return {
        className: 'bg-red-100 text-red-800',
        label: `${percentage}% - Low Confidence`,
      };
    }
  };

  /**
   * Get document status badge
   */
  const getDocumentStatusBadge = (doc: DocumentWithFields) => {
    const base = 'px-2 py-1 rounded text-xs font-medium';
    
    if (isDocumentVerified(doc) || doc.status === "REVIEW_COMPLETED") {
      return `${base} bg-green-100 text-green-800`;
    }
    if (doc.confidenceScore < 0.8 || doc.status === "REVIEW_REQUIRED") {
      return `${base} bg-yellow-100 text-yellow-800`;
    }
    return `${base} bg-blue-100 text-blue-800`;
  };

  /**
   * Get document status label
   */
  const getDocumentStatusLabel = (doc: DocumentWithFields) => {
    if (isDocumentVerified(doc) || doc.status === "REVIEW_COMPLETED") {
      return 'Verified';
    }
    if (doc.confidenceScore < 0.8 || doc.status === "REVIEW_REQUIRED") {
      return 'Review Required';
    }
    return 'Pending';
  };


  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px]">
        <Loader className="h-10 w-10 text-blue-500 animate-spin mb-4" />
        <p className="text-gray-600">Loading documents for verification...</p>
      </div>
    );
  }

  // Render
  return (
    <div className="space-y-6">
      {/* Step Status Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Document Verification</h2>
          <p className="mt-1 text-sm text-gray-600">
            {isStepNotStarted && 'Review and verify extracted document data before proceeding.'}
            {isStepInProgress && 'Verification in progress. Review documents requiring attention.'}
            {isStepCompleted && 'All documents have been verified successfully.'}
          </p>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="ml-3 text-sm text-white">{error}</p>
          </div>
        </div>
      )}

      {/* Completed Banner */}
      {isStepCompleted && (
        <div className="border rounded-xl p-5 backdrop-blur-sm bg-green-50 border-green-200">
          <div className="flex items-start space-x-4">
            <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-green-800">
                Document Verification Complete
              </p>
              <p className="mt-1 text-xs text-green-700">
                All {totalDocuments} document{totalDocuments !== 1 ? 's' : ''} have been verified. You can now proceed to {jobType === JobType.BA ? 'Underwriting Guideline Review' : 'Investor Guideline Review'}.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Guideline Selection ── */}
      <div className="bg-white shadow-xl rounded-xl border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Shield className="h-5 w-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-900">Select Guideline</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Choose a guideline to run document and data consistency checks against.
        </p>

        {loadingGuidelines ? (
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Loader className="h-4 w-4 animate-spin" />
            <span>Loading guidelines...</span>
          </div>
        ) : guidelines.length === 0 ? (
          <p className="text-sm text-gray-500 italic">
            {jobType === JobType.BA
              ? 'No Borrower Analysis guidelines available. Contact your administrator to add guidelines.'
              : 'No guidelines configured. Contact your administrator.'}
          </p>
        ) : (
          <div className="flex-1">
            <label htmlFor="guideline-select" className="block text-sm font-medium text-gray-700 mb-1">
              Guideline
            </label>
            <select
              id="guideline-select"
              value={selectedGuidelineId}
              onChange={e => setSelectedGuidelineId(e.target.value)}
              disabled={verifying || consistencyVerifying}
              className="w-full rounded-lg border border-gray-300 bg-white text-gray-900 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              <option value="">— Select a guideline —</option>
              {guidelines.map(g => (
                <option key={g.guidelineId} value={g.guidelineId}>{g.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* ── Guideline Document Verification Panel ── */}
      <div className="bg-white shadow-xl rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Shield className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Guideline Document Check</h3>
          </div>
          <button
            onClick={handleRunVerification}
            disabled={!selectedGuidelineId || verifying || loadingCachedResults}
            className={`inline-flex items-center px-5 py-2 rounded-lg font-medium text-sm transition-colors ${
              !selectedGuidelineId || verifying || loadingCachedResults
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {verifying ? (
              <><Loader className="h-4 w-4 mr-2 animate-spin" />Verifying...</>
            ) : loadingCachedResults ? (
              <><Loader className="h-4 w-4 mr-2 animate-spin" />Loading...</>
            ) : (
              <><Shield className="h-4 w-4 mr-2" />{verificationResults ? 'Re-run Check' : 'Run Check'}</>
            )}
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Verify that all required documents for the selected investor guideline are present and valid.
        </p>

        {/* Verification error */}
        {verificationError && (
          <div className="mt-4 flex items-start space-x-2 text-sm text-red-600">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{verificationError}</span>
          </div>
        )}

        {/* Verification results */}
        {verificationResults && (
          <div className="mt-5 space-y-2">
            <p className="text-sm font-medium text-gray-700">Results:</p>
            {Object.entries(verificationResults).map(([docType, result]) => (
              <div
                key={docType}
                className={`flex items-start justify-between rounded-lg border px-4 py-3 ${
                  result.status
                    ? 'border-green-200 bg-green-50'
                    : 'border-red-200 bg-red-50'
                }`}
              >
                <div className="flex items-center space-x-3">
                  {result.status ? (
                    <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">{docType}</p>
                    {!result.status && result.reason && (
                      <p className="text-xs text-red-600 mt-0.5">{result.reason}</p>
                    )}
                  </div>
                </div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                  result.status
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {result.status ? 'Pass' : 'Fail'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Data Consistency Verification Panel ── */}
      <div className="bg-white shadow-xl rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Shield className="h-5 w-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-900">Data Consistency Check</h3>
          </div>
          <button
            onClick={handleRunConsistencyVerification}
            disabled={!selectedGuidelineId || consistencyVerifying || loadingCachedConsistencyResults}
            className={`inline-flex items-center px-5 py-2 rounded-lg font-medium text-sm transition-colors ${
              !selectedGuidelineId || consistencyVerifying || loadingCachedConsistencyResults
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-700 text-white'
            }`}
          >
            {consistencyVerifying ? (
              <><Loader className="h-4 w-4 mr-2 animate-spin" />Verifying...</>
            ) : loadingCachedConsistencyResults ? (
              <><Loader className="h-4 w-4 mr-2 animate-spin" />Loading...</>
            ) : (
              <><Shield className="h-4 w-4 mr-2" />{consistencyResults ? 'Re-run Check' : 'Run Check'}</>
            )}
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Verify that data across documents is consistent (e.g., borrower names, income figures, dates).
        </p>

        {/* Consistency error */}
        {consistencyError && (
          <div className="mt-4 flex items-start space-x-2 text-sm text-red-600">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{consistencyError}</span>
          </div>
        )}

        {/* Consistency results */}
        {consistencyResults && (
          <div className="mt-5 space-y-2">
            <p className="text-sm font-medium text-gray-700">Results:</p>
            {Object.entries(consistencyResults).map(([checkName, result]) => (
              <div
                key={checkName}
                className={`flex items-start justify-between rounded-lg border px-4 py-3 ${
                  result.status
                    ? 'border-green-200 bg-green-50'
                    : 'border-red-200 bg-red-50'
                }`}
              >
                <div className="flex items-center space-x-3">
                  {result.status ? (
                    <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {checkName.replace(/_/g, ' ')}
                    </p>
                    {result.reason && (
                      <p className={`text-xs mt-0.5 ${
                        result.status
                          ? 'text-green-700'
                          : 'text-red-600'
                      }`}>{result.reason}</p>
                    )}
                  </div>
                </div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                  result.status
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {result.status ? 'Pass' : 'Fail'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Navigation buttons */}
      {(isStepCompleted || true) && (
        <div className="flex justify-between mt-4">
          {onBack && (
            <button
              onClick={onBack}
              className="inline-flex items-center px-6 py-3 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-all"
            >
              <ArrowRight className="h-4 w-4 mr-2 rotate-180" />
              Back: Document Processing
            </button>
          )}
          <button
            onClick={async () => {
              try {
                const session = await fetchAuthSession();
                const tenantId = session.tokens?.idToken?.payload['custom:tenantId'] as string || 'default-tenant';
                await client.graphql({
                  query: mutations.completeStep,
                  variables: { jobId: job.jobId, tenantId, step: 'DOCUMENT_VERIFICATION', jobType },
                });
              } catch (err) {
                console.error('Failed to update job status:', err);
              }
              onStepComplete();
            }}
            className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all ml-auto"
          >
            Next: {jobType === JobType.BA ? 'Underwriting Guideline Review' : 'Investor Guideline Review'}
            <ArrowRight className="h-4 w-4 ml-2" />
          </button>
        </div>
      )}

      {/* Document Viewer Modal */}
      {selectedDocument && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setSelectedDocument(null)}
            />
            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  {selectedDocument.sourceFilename}
                </h3>
                <button
                  onClick={() => setSelectedDocument(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="px-6 py-4 min-h-[400px] flex items-center justify-center bg-gray-100">
                <div className="text-center">
                  <FileText className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                  <p className="text-gray-600">
                    Document preview would be displayed here
                  </p>
                  {selectedDocument.processedDataS3Key && (
                    <button
                      className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open in New Tab
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


/**
 * FieldVerificationRow - Individual field with verification actions
 * Requirement 7.4: Accept, edit, reject actions for each field
 * Requirement 7.5: Update confidence to 100% on edit
 */
interface FieldVerificationRowProps {
  field: ExtractedField;
  fieldIndex: number;
  documentId: string;
  onAction: (documentId: string, fieldIndex: number, action: FieldUpdateAction, editedValue?: string) => void;
  disabled: boolean;
}

const FieldVerificationRow: React.FC<FieldVerificationRowProps> = ({
  field,
  fieldIndex,
  documentId,
  onAction,
  disabled,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(field.value);

  const confidencePercentage = Math.round(field.confidence * 100);
  const isLowConfidence = field.confidence < 0.8;

  const handleAccept = () => {
    onAction(documentId, fieldIndex, 'ACCEPT');
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditValue(field.editedValue || field.value);
  };

  const handleSaveEdit = () => {
    onAction(documentId, fieldIndex, 'EDIT', editValue);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditValue(field.value);
  };

  const handleReject = () => {
    onAction(documentId, fieldIndex, 'REJECT');
  };

  // Get action indicator
  const getActionIndicator = () => {
    if (!field.action) return null;
    
    switch (field.action) {
      case 'ACCEPT':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
            <Check className="h-3 w-3 mr-1" />
            Accepted
          </span>
        );
      case 'EDIT':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
            <Edit2 className="h-3 w-3 mr-1" />
            Edited (100%)
          </span>
        );
      case 'REJECT':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={`p-3 rounded-lg border ${
        isLowConfidence && !field.action
          ? 'border-yellow-300 bg-yellow-50/50'
          : 'border-gray-200 bg-gray-50'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {field.field}
            </span>
            {isLowConfidence && !field.action && (
              <AlertTriangle className="h-3 w-3 text-yellow-500" />
            )}
          </div>
          
          {isEditing ? (
            <div className="mt-1 flex items-center space-x-2">
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                autoFocus
              />
              <button
                onClick={handleSaveEdit}
                className="p-1 text-green-600 hover:text-green-700"
                title="Save"
              >
                <Check className="h-4 w-4" />
              </button>
              <button
                onClick={handleCancelEdit}
                className="p-1 text-gray-400 hover:text-gray-500"
                title="Cancel"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <p className={`mt-1 text-sm font-medium ${
              field.action === 'REJECT' 
                ? 'text-red-600 line-through' 
                : 'text-gray-900'
            }`}>
              {field.action === 'EDIT' ? field.editedValue : field.value}
            </p>
          )}
          
          <div className="mt-1 flex items-center space-x-2">
            <span className={`text-xs ${
              isLowConfidence ? 'text-yellow-600' : 'text-gray-500'
            }`}>
              Confidence: {field.action === 'EDIT' ? '100%' : `${confidencePercentage}%`}
            </span>
            {getActionIndicator()}
          </div>
        </div>

        {/* Action Buttons - Requirement 7.4 */}
        {!disabled && !field.action && !isEditing && (
          <div className="flex items-center space-x-1 ml-3">
            <button
              onClick={handleAccept}
              className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-100 rounded transition-colors"
              title="Accept"
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              onClick={handleEdit}
              className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded transition-colors"
              title="Edit"
            >
              <Edit2 className="h-4 w-4" />
            </button>
            <button
              onClick={handleReject}
              className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-100 rounded transition-colors"
              title="Reject"
            >
              <XCircle className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentVerificationStep;
